import React, { useContext, useEffect, useState } from "react";
import { Box, Container, Sheet, Stack, Typography, CircularProgress, Button, Alert } from "@mui/joy";
import { Navbar } from "./Navbar";
import { AuthContext } from "../utils/AuthProvider";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import { APIurl } from "../utils/Utils";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Refresh, ArrowBack } from "@mui/icons-material";

export function UpdatePaymentMethod() {
    const { t } = useTranslation();
    const { userData } = useContext(AuthContext);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!userData?.uid) return;
        createUpdatePaymentMethodLink();
    }, [userData?.uid]);

    async function createUpdatePaymentMethodLink() {
        try {
            setLoading(true);
            setError(null);

            const user = firebase.auth().currentUser;
            if (!user) {
                throw new Error(t("usernotauthenticated"));
            }

            const token = await user.getIdToken();

            const response = await fetch(`${APIurl}/createUpdatePaymentMethodLink/${userData.uid}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(t("updatepaymentmethodlinkerror"));
            }

            const data = await response.json();

            if (!data?.url) {
                throw new Error(t("stripelinknotavailable"));
            }

            window.location.href = data.url;
        } catch (e: unknown) {
            if (e instanceof Error) {
                setError(e.message);
            } else {
                setError(t("genericerror"));
            }
        } finally {
            setLoading(false);
        }
    }

    return (
        <Box sx={{ minHeight: "100vh" }}>
            <Navbar />
            <Container sx={{ py: { xs: "7rem", md: "10rem" } }}>
                <Sheet variant="outlined" sx={{ maxWidth: 700, mx: "auto", p: 4, borderRadius: 10 }}>
                    <Stack spacing={3} alignItems="center" textAlign="center">
                        {loading && (
                            <>
                                <CircularProgress size="lg" />
                                <Stack spacing={1}>
                                    <Typography level="h3">{t("redirectingtostripe")}</Typography>
                                    <Typography level="body-md">{t("redirectingtostripelabel")}</Typography>
                                </Stack>
                            </>
                        )}

                        {!loading && error && (
                            <>
                                <Alert color="danger" variant="soft">
                                    <Stack spacing={1}>
                                        <Typography level="title-md">{t("unabletoredirect")}</Typography>
                                        <Typography level="body-sm">{error}</Typography>
                                    </Stack>
                                </Alert>

                                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                    <Button
                                        variant="solid"
                                        color="primary"
                                        startDecorator={<Refresh />}
                                        onClick={createUpdatePaymentMethodLink}
                                    >
                                        {t("retry")}
                                    </Button>

                                    <Link to="/account">
                                        <Button variant="outlined" color="neutral" startDecorator={<ArrowBack />}>
                                            {t("backtoaccount")}
                                        </Button>
                                    </Link>
                                </Stack>
                            </>
                        )}
                    </Stack>
                </Sheet>
            </Container>
        </Box>
    );
}